// Single source of truth for per-route SEO metadata.
// Used by the pages (client-side <SEO>) and by scripts/generate-static.js, which
// writes the same values into each route's prerendered HTML so crawlers see the
// right title / description / canonical before any JavaScript runs.
//
// URLs have NO trailing slash: Cloudflare Pages serves dist/<route>.html at
// /<route> and 308-redirects /<route>/ to it. Keep canonicals and the sitemap
// in this form, otherwise Search Console reports "Page with redirect".

export const SITE_URL = 'https://codes.techkarmic.com';

export interface RouteSeo {
  /** Short name for navigation links. */
  label: string;
  title: string;
  description: string;
}

export const seoRoutes: Record<string, RouteSeo> = {
  home: {
    label: 'Home',
    title: 'Indian Codes Hub - PIN Codes, IFSC Codes, STD Codes & More | Complete Database',
    description:
      'Find all Indian codes in one place - PIN codes, bank IFSC codes, STD codes, RTO codes, railway station codes, USSD codes, and more. Fast search with copy functionality.',
  },
  'pin-codes': {
    label: 'PIN Codes',
    title: 'Indian PIN Codes - Complete Postal Code Database | Indian Codes Hub',
    description:
      'Find Indian PIN codes (postal codes) by city, district, or state. Complete database of 6-digit postal codes with copy functionality. Fast and accurate PIN code search.',
  },
  'ifsc-codes': {
    label: 'IFSC Codes',
    title: 'Bank IFSC Codes India - Complete IFSC Code Database | Indian Codes Hub',
    description:
      'Find Indian bank IFSC codes for NEFT, RTGS, and IMPS transfers. Search by bank name, branch, or location. Copy IFSC codes instantly with one click.',
  },
  'std-codes': {
    label: 'STD Codes',
    title: 'Indian STD Codes - Telephone Area Codes Database | Indian Codes Hub',
    description:
      'Find Indian STD (Standard Trunk Dialing) codes for all cities. Complete telephone area codes database with instant copy functionality. Search by city or state.',
  },
  'rto-codes': {
    label: 'RTO Codes',
    title: 'RTO Codes India - Complete Vehicle Registration Database | Indian Codes Hub',
    description:
      'Search for Indian RTO (Regional Transport Office) codes by state or city. Complete vehicle registration prefix database with copy functionality.',
  },
  'railway-codes': {
    label: 'Railway Codes',
    title: 'Indian Railway Station Codes - Complete Station Database | Indian Codes Hub',
    description:
      'Search for Indian Railway station codes by station name, city, state, or zone. Complete database of station codes with copy functionality.',
  },
  'ussd-codes': {
    label: 'USSD Codes',
    title: 'USSD Codes India - Telecom Mobile Shortcuts Database | Indian Codes Hub',
    description:
      'Search for USSD codes for all Indian telecom operators. Find mobile shortcodes for balance check, internet, recharge, and caller tunes.',
  },
  'html-colors': {
    label: 'HTML Colors',
    title: 'HTML Colors & Unicode Symbols Database | Indian Codes Hub',
    description:
      'Browse, pick, and search HTML colors with HEX/RGB formats and Unicode characters. Complete reference database with instant copy functionality.',
  },
  festivals: {
    label: 'Festivals',
    title: 'Indian Festivals & Holidays Calendar - Holiday Dates | Indian Codes Hub',
    description:
      'Complete database of Indian festivals, gazetted holidays, and national holidays. Explore festival dates and descriptions with calendar view.',
  },
  cricket: {
    label: 'Cricket',
    title: 'Cricket Winners History - Complete IPL & ICC Records | Indian Codes Hub',
    description:
      'Browse winners, runners-up, venues, and years for major cricket tournaments including IPL and ICC events (ODI World Cup, T20 World Cup, WTC).',
  },
};

/** Route slugs that have their own page (everything except home). */
export const pageSlugs = Object.keys(seoRoutes).filter((slug) => slug !== 'home');

/** Canonical URL for a slug: no trailing slash, root keeps its slash. */
export const canonicalFor = (slug: string): string =>
  slug === 'home' ? `${SITE_URL}/` : `${SITE_URL}/${slug}`;

/** Props for the <SEO> component: title, description and canonicalUrl. */
export const routeSeo = (slug: string) => {
  const { title, description } = seoRoutes[slug];
  return { title, description, canonicalUrl: canonicalFor(slug) };
};
